// ─────────────────────────────────────────────────────────────
// Single shared Socket.IO client for the whole app. Every component
// that needs socket access (support chat, the floating support
// button, future features — transaction push-updates, notifications,
// presence, anything else) should go through this hook rather than
// creating its own `io(...)` instance.
//
// WHY A SHARED SINGLETON MATTERS HERE: SupportChat previously declared
// `const socket = io(...)` at module scope inside a page component
// file. That accidentally worked as a de facto singleton ONLY because
// nothing else needed socket access yet — but it meant any other part
// of the app wanting the connection would have to reach into a page
// component's internals to get it (backwards: pages should consume
// shared infrastructure, not BE the shared infrastructure), and two
// independent consumers managing connect()/disconnect() on the same
// instance would fight each other. This hook is the actual shared
// infrastructure; SupportChat.jsx now consumes it instead of owning it.
//
// CONNECTION LIFECYCLE: matches what the existing SupportChat
// component actually did — autoConnect:false, with connect() called
// explicitly. Kept that way here (not switched to always-on
// autoConnect) because socket connections aren't free, and a logged-
// in user simply browsing the app shouldn't necessarily hold an open
// socket unless something on the current page actually needs one. The
// hook tracks how many mounted consumers want the connection open via
// a ref-counted "subscribers" count — connect() fires when the first
// consumer asks for it, disconnect() fires only when the LAST
// consumer requesting it unmounts. This lets multiple components
// (e.g. SupportChat AND FloatingSupportButton, if both mounted at
// once) share one live connection without prematurely killing it out
// from under each other.
//
// USAGE:
//   const { on, off, emit, connected } = useSocket(user?.userId);
//   useEffect(() => {
//     const handler = (payload) => { ... };
//     on("transactionUpdate", handler);
//     return () => off("transactionUpdate", handler);
//   }, [on, off]);
//
// Pass `userId` whenever the consumer wants the connection open AND
// registered as that user (joins support_${userId} / tx_${userId}
// rooms server-side, per socketHandlers.js's registerUser handler).
// Pass nothing/null if a component just wants to listen to an
// ALREADY-open connection without itself driving connect/disconnect
// (rare — most consumers should pass userId).
// ─────────────────────────────────────────────────────────────
import { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || "https://serve.easwallet.com";

let sharedSocket = null;
let subscriberCount = 0;

function getSocket() {
  if (!sharedSocket) {
    sharedSocket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      autoConnect: false,
      reconnection: true,
    });
  }
  return sharedSocket;
}

export function useSocket(userId) {
  const socketRef = useRef(getSocket());
  const [connected, setConnected] = useState(socketRef.current.connected);

  useEffect(() => {
    const socket = socketRef.current;

    const handleConnect = () => {
      setConnected(true);
      // Re-register (and rejoin rooms server-side) on every connect,
      // including reconnects after a dropped connection — the server
      // doesn't remember room membership across a fresh connection.
      if (userId) {
        socket.emit("registerUser", { userId, role: "user" });
      }
    };
    const handleDisconnect = () => setConnected(false);

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    // ── Ref-counted connect/disconnect ──────────────────────
    // Only actually open the connection if this consumer passed a
    // userId (i.e. wants to be an active participant, not just a
    // passive listener on a connection someone else is driving).
    let countedAsSubscriber = false;
    if (userId) {
      subscriberCount += 1;
      countedAsSubscriber = true;
      if (!socket.connected && !socket.connecting) {
        socket.connect();
      } else if (socket.connected) {
        // Already connected (e.g. a second component mounted after
        // the first already opened it) — register immediately rather
        // than waiting for a "connect" event that already fired.
        handleConnect();
      }
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);

      if (countedAsSubscriber) {
        subscriberCount = Math.max(0, subscriberCount - 1);
        // Only disconnect once NO mounted consumer still wants the
        // connection open — prevents one component's unmount from
        // killing the connection out from under a sibling that's
        // still using it (e.g. FloatingSupportButton closing while
        // the full Support page chat is also open in another part of
        // the tree, however unlikely that specific case is today —
        // the point is this is correct regardless of how many
        // consumers end up existing as the app grows).
        if (subscriberCount === 0) {
          socket.disconnect();
        }
      }
    };
  }, [userId]);

  const on = useCallback((event, handler) => {
    socketRef.current.on(event, handler);
  }, []);

  const off = useCallback((event, handler) => {
    socketRef.current.off(event, handler);
  }, []);

  const emit = useCallback((event, payload) => {
    socketRef.current.emit(event, payload);
  }, []);

  return { socket: socketRef.current, connected, on, off, emit };
}